import { useEffect, useRef, useState } from "react";
import { getLogger } from "@hoteltradergit/ht-telemetry-ui-lib";
import PropTypes from "prop-types";
import { InputPicker } from "rsuite";
import { searchUalUsers } from "../../api";

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
const DEFAULT_LIMIT = 20;

/**
 * Async user picker for the User Name / User Email filters. Types ahead against
 * GET /ual/users; the chosen user resolves to an {@code actor_id} (its {@code id}).
 * {@code value} is the selected user object ({ id, name, email }) or null.
 */
const UserTypeahead = ({ value, onChange, displayBy, placeholder }) => {
  const [options, setOptions] = useState([]);
  const timer = useRef(null);

  // Map results to picker options; keep the current selection visible even after
  // the option list changes on a new search.
  const data = options.map((u) => ({ ...u, label: u[displayBy], value: u.id }));
  if (value && !data.some((o) => o.value === value.id)) {
    data.unshift({ ...value, label: value[displayBy], value: value.id });
  }

  // AppResponse envelope: the user list lives in data.data.
  const fetchUsers = async (keyword) => {
    try {
      const res = await searchUalUsers(keyword, DEFAULT_LIMIT);
      setOptions(res?.data?.data || []);
    } catch (error) {
      // An empty list is the right UI outcome, but the failure still needs to be visible —
      // a typeahead that silently returns nothing looks identical to "no matching users".
      getLogger().error("ual.user_search_failed", {
        type: "Error",
        page_url: window.location.href,
        error_desc: error?.message ?? String(error),
      });
      setOptions([]);
    }
  };

  // Load a default page of users on mount — same as the Hotel Name picker
  // (usePropertyPickerOptions fetches its first page on mount) — so the list is
  // populated up-front, before the user opens it or types.
  useEffect(() => {
    fetchUsers("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (keyword) => {
    clearTimeout(timer.current);
    const trimmed = (keyword || "").trim();
    if (!trimmed) {
      fetchUsers(""); // back to the default 20 when the box is cleared
      return;
    }
    if (trimmed.length < MIN_CHARS) {
      return; // 1 char — keep the current list until it's a real search
    }
    timer.current = setTimeout(() => fetchUsers(trimmed), DEBOUNCE_MS);
  };

  return (
    <InputPicker
      block
      data={data}
      value={value ? value.id : null}
      placeholder={placeholder}
      onSearch={handleSearch}
      onSelect={(val, item) =>
        onChange({ id: item.id, name: item.name, email: item.email })
      }
      onClean={() => onChange(null)}
      container={() => document.body}
    />
  );
};

UserTypeahead.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  displayBy: PropTypes.oneOf(["name", "email"]).isRequired,
  placeholder: PropTypes.string,
};

UserTypeahead.defaultProps = {
  value: null,
  placeholder: "",
};

export default UserTypeahead;
